import { LuxLocalizationProvider, LuxThemeProvider } from '@gemini/fe-ds-core';
import { FEObTokens } from '@static/FE/FE.obTokens';
import { LCObTokens } from '@static/LC/LC.obTokens';
import { obTokensValues } from '@static/obTokensValues';
import { InfoUser, useOnlineBookingStore } from '@store/onlineBookingStore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrandNameList } from '@utils/consts/brandNameList';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  BrowserRouter,
  HashRouter,
  Router,
  RouterProps,
} from 'react-router-dom';
import { Modes } from '@utils/enums/Modes.enum';
import { UrlsProvider } from '@utils/contexts/UrlsContext';
import App from '../App';
import { resetApp } from '@utils/functions';
import Environment from '../Environment';
import { TOObTokens } from '@static/TO/TO.obTokens';
import sofiaProRegular from '@assets/fonts/Sofia-Pro-Regular.woff';
import sofiaProSemiBold from '@assets/fonts/Sofia-Pro-Semi-Bold.woff';
import sofiaProBold from '@assets/fonts/Sofia-Pro-Bold.woff';
import minervaModernRegular from '@assets/fonts/Minerva-Modern-Regular.woff';
import minervaModernBold from '@assets/fonts/Minerva-Modern-Bold.woff';
import { generateTheme, LuxPaletteTypes, palette } from '@gemini/fe-ds-themes';
import helveticaForTarget from '@assets/fonts/Helvetica-For-Target.otf';
import helveticaForTargetBold from '@assets/fonts/Helvetica-For-Target-Bold.woff';
import poppinsRegular from '@assets/fonts/Poppins-Regular.ttf';
import poppinsSemiBold from '@assets/fonts/Poppins-SemiBold.ttf';
import poppinsBold from '@assets/fonts/Poppins-Bold.ttf';
import { FlowModes } from '@utils/enums/FlowModes.enum';
import {
  IntegrationModes,
  isEmbeddedIntegrationMode,
} from '@utils/enums/IntegrationModes.enum';
import { Links } from 'src/embedded-app/EmbeddedApp';

export interface CommonConfigurationProps {
  basePath: string;
  mode: Modes;
  entrySource?: string;
  practiceId?: string;
  providerId?: string;
  appointmentType?: string;
  brandName?: string;
  jwtToken?: string;
  infoUser?: InfoUser;
  hideHeader?: boolean;
  environmentConfig?: Record<string, string>;
  envConfigUrl?: string;
  collapsableMobileBookingDetails?: boolean;
  appointmentId?: string;
  flowMode?: FlowModes;
  integrationMode?: IntegrationModes;
  rescheduleUrl?: string;
  cancelUrl?: string;
  pageCountry?: string;
  pageLanguage?: string;
  links?: Links;
  hideBackToBrandHomePage?: boolean;
  onContentUpdate?: (step: string) => void;
  refid?: string;
  readOnlyPrefilledPatientsDetailsFields?: boolean;
  history?: RouterProps['history'];
}

const queryClient = new QueryClient();

/**
 * CommonConfiguration is the main component that wraps the application and provides the necessary context for the application to work.
 * It is used in both standalone and embedded modes.
 * @param basePath - The base path of the application.
 * @param mode - The mode of the application.
 * @param practiceId - The practice id.
 * @param providerId - The provider id.
 * @param appointmentType - The appointment type.
 * @param brandName - The brand name.
 * @param jwtToken - The jwt token.
 * @param hideHeader - Whether to hide the header.
 * @param collapsableMobileBookingDetails - Whether to enable collapsable mobile booking details.
 * @param environmentConfig - The environment configuration.
 * @param infoUser - The user information.
 * @param appointmentId - The appointment id.
 * @param flowMode - The flow mode (default, reschedule, cancel, confirm).
 * @param integrationMode - The integration mode (standalone, embedded; wcs is supported as a legacy alias).
 * @param rescheduleUrl - The URL for rescheduling flow.
 * @param cancelUrl - The URL for canceling flow.
 * @param pageCountry - The country code.
 * @param pageLanguage - The language code.
 * @param links - Custom links for the application (e.g., terms and conditions, privacy policy).
 * @param hideBackToBrandHomePage - Boolean to hide back to LC/TO home page CTA in cancellation flow.
 * @param onContentUpdate - Optional callback triggered after each logical route/step content update.
 * @param refid - optional refid for GL purposes.
 */
function CommonConfiguration({
  basePath,
  mode,
  entrySource,
  practiceId,
  providerId,
  appointmentType,
  brandName,
  jwtToken,
  infoUser,
  integrationMode = IntegrationModes.STANDALONE,
  hideHeader = false,
  environmentConfig,
  envConfigUrl,
  appointmentId,
  rescheduleUrl,
  cancelUrl,
  flowMode = FlowModes.DEFAULT,
  pageCountry,
  pageLanguage,
  links,
  hideBackToBrandHomePage = false,
  onContentUpdate,
  refid,
  readOnlyPrefilledPatientsDetailsFields,
  history,
}: CommonConfigurationProps) {
  const colorConfig = useOnlineBookingStore(
    state => state.generalConfig?.customization,
  );
  const currentPracticeId = useOnlineBookingStore(state => state.practiceId);
  const setRefid = useOnlineBookingStore(state => state.setRefid);
  const setPracticeId = useOnlineBookingStore(state => state.setPracticeId);
  const setAppointmentId = useOnlineBookingStore(
    state => state.setAppointmentId,
  );
  const setProviderIdFromUrl = useOnlineBookingStore(
    state => state.setProviderIdFromUrl,
  );
  const setAppTypeIdFromUrl = useOnlineBookingStore(
    state => state.setAppTypeIdFromUrl,
  );
  const setFlowMode = useOnlineBookingStore(state => state.setFlowMode);
  const setIntegrationMode = useOnlineBookingStore(
    state => state.setIntegrationMode,
  );
  const setIsPatientPortalEntry = useOnlineBookingStore(
    state => state.setIsPatientPortalEntry,
  );
  const setBrandName = useOnlineBookingStore(state => state.setBrandName);
  const setJwtToken = useOnlineBookingStore(state => state.setJwtToken);
  const setInfoUser = useOnlineBookingStore(state => state.setInfoUser);

  const setRescheduleUrl = useOnlineBookingStore(
    state => state.setRescheduleUrl,
  );
  const setCancelUrl = useOnlineBookingStore(state => state.setCancelUrl);
  const brandNameFromStore = useOnlineBookingStore(state => state.brandName);

  const setPageCountry = useOnlineBookingStore(state => state.setPageCountry);
  const setPageLanguage = useOnlineBookingStore(state => state.setPageLanguage);
  const setLinks = useOnlineBookingStore(state => state.setLinks);
  const setHideBackToBrandHomePage = useOnlineBookingStore(
    state => state.setHideBackToBrandHomePage,
  );
  const setOnContentUpdate = useOnlineBookingStore(
    state => state.setOnContentUpdate,
  );
  const setReadOnlyPrefilledPatientsDetailsFields = useOnlineBookingStore(
    state => state.setReadOnlyPrefilledPatientsDetailsFields,
  );

  useEffect(() => {
    /**
     * If the practice id is provided, we need to reset the app and set the practice id, brand name, provider id, appointment type and jwt token.
     * We expect all the parameters to be provided at the same time.
     */
    if (practiceId) {
      if (practiceId !== currentPracticeId) {
        resetApp();
      }
      setFlowMode(flowMode);
      setIntegrationMode(integrationMode);
      setIsPatientPortalEntry(entrySource === 'patientportal');
      setPracticeId(practiceId);
      setAppointmentId(appointmentId);
      setBrandName(brandName);
      setProviderIdFromUrl(providerId);
      setAppTypeIdFromUrl(appointmentType);
      setJwtToken(jwtToken);
      setInfoUser(infoUser);
      setRescheduleUrl(rescheduleUrl);
      setCancelUrl(cancelUrl);
      setPageCountry(pageCountry);
      setPageLanguage(pageLanguage);
      setLinks(links);
      setHideBackToBrandHomePage(hideBackToBrandHomePage);
      setOnContentUpdate(onContentUpdate);
      setRefid(refid);
      setReadOnlyPrefilledPatientsDetailsFields(
        readOnlyPrefilledPatientsDetailsFields,
      );
    }
  }, [
    currentPracticeId,
    practiceId,
    brandName,
    providerId,
    appointmentType,
    jwtToken,
    infoUser,
    rescheduleUrl,
    cancelUrl,
    pageCountry,
    pageLanguage,
    onContentUpdate,
    hideBackToBrandHomePage,
    refid,
    readOnlyPrefilledPatientsDetailsFields,
    entrySource,
    setFlowMode,
    setIsPatientPortalEntry,
    setPracticeId,
    setAppointmentId,
    setBrandName,
    setProviderIdFromUrl,
    setAppTypeIdFromUrl,
    setJwtToken,
    setInfoUser,
    setRescheduleUrl,
    setCancelUrl,
    setPageCountry,
    setPageLanguage,
    setLinks,
    setHideBackToBrandHomePage,
    setOnContentUpdate,
    setRefid,
    setReadOnlyPrefilledPatientsDetailsFields,
  ]);

  /**
   * Returns the tokens for the current brand.
   */
  const tokens = useCallback(() => {
    console.log('LCObTokens', brandNameFromStore);
    switch (brandNameFromStore) {
      case BrandNameList.FE:
        return FEObTokens;
      case BrandNameList.LC:
        return LCObTokens;
      case BrandNameList.TO:
        return TOObTokens;
      default:
        return obTokensValues(colorConfig);
    }
  }, [brandNameFromStore, colorConfig]);

  const theme = useMemo(() => {
    const obTokens: any = tokens();
    const currentPalette: LuxPaletteTypes = {
      ...palette.LUX,
      obTokens,
      applicationName: 'OnlineBooking',
    };
    const currentTheme = generateTheme(currentPalette);
    /**
     * Same breakpoints for all brands including OB-VX
     */
    currentTheme.breakpoints.values = {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 1024,
      xl: 1440,
    };

    /**
     * Custom typography for brandName for TAB enhancement
     */
    if (brandNameFromStore) {
      /**
       * The typography is the same for all brands, but the font family is different.
       * We need to override the font family for the current brand.
       */
      currentTheme.typography = {
        ...currentTheme.typography,
        textMedium: {
          fontFamily: obTokens.textBaseFamily,
          fontSize: obTokens.textSmSize,
          fontWeight: '700',
          lineHeight: obTokens.textSmLineHeight,
          letterSpacing: obTokens.textSmKerning,
        },
        textMdRegular: {
          fontFamily: obTokens.textBaseFamily,
          fontSize: obTokens.textSmSize,
          fontWeight: '400',
          lineHeight: obTokens.textSmLineHeight,
          letterSpacing: obTokens.textSmKerning,
        },
        heading4: {
          fontFamily: obTokens.textH4Family,
          fontSize: obTokens.textH4DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH4DeskLineHeight,
          letterSpacing: obTokens.textH4DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH4MobSize,
            lineHeight: obTokens.textH4MobLineHeight,
            letterSpacing: obTokens.textH4MobKerning,
          },
        },
        heading3: {
          fontFamily: obTokens.textH3Family,
          fontSize: obTokens.textH3DeskSize,
          fontWeight:
            brandName === BrandNameList.LC || brandName === BrandNameList.FE
              ? '400'
              : '700',
          lineHeight: obTokens.textH3DeskLineHeight,
          letterSpacing: obTokens.textH3DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH3MobSize,
            lineHeight: obTokens.textH3MobLineHeight,
            letterSpacing: obTokens.textH3MobKerning,
          },
        },
        heading2: {
          fontFamily: obTokens.textH2Family,
          fontSize: obTokens.textH2DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH2DeskLineHeight,
          letterSpacing: obTokens.textH2DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH2MobSize,
            lineHeight: obTokens.textH2MobLineHeight,
            letterSpacing: obTokens.textH2MobKerning,
          },
        },
        heading1: {
          fontFamily: obTokens.textH1Family,
          fontSize: obTokens.textH1DeskSize,
          fontWeight: '700',
          lineHeight: obTokens.textH1DeskLineHeight,
          letterSpacing: obTokens.textH1DeskKerning,
          [currentTheme.breakpoints.down('sm')]: {
            fontSize: obTokens.textH1MobSize,
            lineHeight: obTokens.textH1MobLineHeight,
            letterSpacing: obTokens.textH1MobKerning,
          },
        },
        textXs: {
          fontFamily: obTokens.textXsFamily,
          fontSize: obTokens.textXsSize,
          fontWeight: '400',
          lineHeight: obTokens.textXsLineHeight,
          letterSpacing: obTokens.textXsKerning,
        },
        textMdXs: {
          fontFamily: obTokens.textXsFamily,
          fontSize: obTokens.textXsSize,
          fontWeight: '700',
          lineHeight: obTokens.textXsLineHeight,
          letterSpacing: obTokens.textXsKerning,
        },
      };
      /**
       * Custom font for brandName for TAB enhancement
       */
      const muiCssBaseline = currentTheme.components?.MuiCssBaseline;
      const cssBaselineStyleOverrides = muiCssBaseline?.styleOverrides;
      if (muiCssBaseline && typeof cssBaselineStyleOverrides === 'string') {
        /**
         * the value of fontFamily needs to be the same as the value of the fontFamily in the tokens.
         */
        if (brandNameFromStore === BrandNameList.LC) {
          muiCssBaseline.styleOverrides = `${cssBaselineStyleOverrides}
              body {
                font-family: ${obTokens.textBaseFamily};
                font-size: ${obTokens.textBaseSize};
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProRegular}) format('woff');
                font-weight: normal;
                font-style: normal;
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProSemiBold}) format('woff');
                font-weight: 600;
                font-style: normal;
              }
              @font-face {
                font-family: 'sofia pro';
                src: url(${sofiaProBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
              @font-face {
                font-family: 'minerva-modern';
                src: url(${minervaModernRegular}) format('woff');
                font-weight: 400;
                font-style: normal;
              }
              @font-face {
                font-family: 'minerva-modern';
                src: url(${minervaModernBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
          `;
        } else if (brandNameFromStore === BrandNameList.FE) {
          muiCssBaseline.styleOverrides = `${cssBaselineStyleOverrides}
              body {
                font-family: ${obTokens.textBaseFamily};
                font-size: ${obTokens.textBaseSize};
              }
              @font-face {
                font-family: 'Poppins';
                src: url(${poppinsRegular}) format('truetype');
                font-weight: 400;
                font-style: normal;
              }
              @font-face {
                font-family: 'Poppins';
                src: url(${poppinsSemiBold}) format('truetype');
                font-weight: 600;
                font-style: normal;
              }
              @font-face {
                font-family: 'Poppins';
                src: url(${poppinsBold}) format('truetype');
                font-weight: 700;
                font-style: normal;
              }
          `;
        } else if (brandNameFromStore === BrandNameList.TO) {
          muiCssBaseline.styleOverrides = `${cssBaselineStyleOverrides}
              body {
                font-family: ${obTokens.textBaseFamily};
                font-size: ${obTokens.textBaseSize};
              }
              @font-face {
                font-family: 'Helvetica for Target';
                src: url(${helveticaForTarget}) format('opentype');
                font-weight: normal;
                font-style: normal;
              }
              @font-face {
                font-family: 'Helvetica for Target';
                src: url(${helveticaForTargetBold}) format('woff');
                font-weight: 700;
                font-style: normal;
              }
          `;
        }
      }
    }

    return currentTheme;
  }, [brandNameFromStore, colorConfig]);

  const renderWithRouter = useCallback(
    (children: ReactNode) => {
      if (history) {
        return <Router history={history}>{children}</Router>;
      }

      if (isEmbeddedIntegrationMode(integrationMode)) {
        return <HashRouter>{children}</HashRouter>;
      }

      return <BrowserRouter>{children}</BrowserRouter>;
    },
    [history, integrationMode],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <LuxThemeProvider theme={theme}>
        <LuxLocalizationProvider>
          {renderWithRouter(
            <Environment
              environmentConfig={environmentConfig}
              brandName={brandName}
              envConfigUrl={envConfigUrl}
            >
              <UrlsProvider
                basePath={
                  isEmbeddedIntegrationMode(integrationMode) ? '' : basePath
                }
              >
                <App mode={mode} hideHeader={hideHeader} />
              </UrlsProvider>
            </Environment>,
          )}
        </LuxLocalizationProvider>
      </LuxThemeProvider>
    </QueryClientProvider>
  );
}

export default CommonConfiguration;
